import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import type { ColumnDef } from "@tanstack/react-table";
import {
  ChevronDown,
  FileType,
  ImageIcon,
  MoreHorizontal,
  Plus,
  Trash2,
  Upload,
} from "lucide-react";
import { toast } from "sonner";
import {
  booksApi,
  fetchAuthBlob,
  DEFAULT_PREVIEW_SETTINGS,
  DEFAULT_SCENE_SETTINGS,
  DEFAULT_NAME_LAYOUT,
  OPENAI_VALUES,
  GEMINI_VALUES,
  SCENE_GEMINI_VALUES,
  type Book,
  type OpenAiImageParams,
  type PreviewAssetKind,
  type PreviewNameLayout,
  type PreviewSettings,
  type SceneOverrides,
  type SceneSettings,
  type BookDetail as BookDetailType,
  type BookScene,
  type BookTitle,
} from "../lib/books-api";
import {
  EMPTY_LABEL,
  OpenAiParamFields,
  extraToText,
  textToExtra,
} from "../components/OpenAiParamFields";
import { useConfirm } from "@/components/confirm-dialog";
import { DataTable } from "@/components/data-table";
import { EmptyState } from "@/components/empty-state";
import {
  AdvancedSection,
  ErrorState,
  FormSection,
  PageSkeleton,
} from "@/components/layout";
import { PageHeader } from "@/components/page-header";
import { SaveBarProvider, useSaveBarForm } from "@/components/save-bar";
import { StatusBadge, type Tone } from "@/components/status-badge";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { useUrlParam } from "@/hooks/use-url-state";

export default function BookDetail() {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [data, setData] = useState<BookDetailType | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [tab, setTab] = useUrlParam("tab", "general");

  const reload = useCallback(() => {
    booksApi
      .get(id)
      .then((d) => {
        setData(d);
        setError(null);
      })
      .catch((err) => setError((err as Error).message));
  }, [id]);

  useEffect(reload, [reload]);

  async function handleDelete() {
    if (!data) return;
    const ok = await confirm({
      title: `Buch „${data.book.title}“ löschen?`,
      description: "Das Buch und alle seine Szenen werden endgültig gelöscht.",
      confirmLabel: "Buch löschen",
      destructive: true,
    });
    if (!ok) return;
    setDeleting(true);
    try {
      await booksApi.delete(data.book.id);
      toast.success("Buch gelöscht");
      navigate("/books");
    } catch (err) {
      toast.error((err as Error).message);
      setDeleting(false);
    }
  }

  if (error && !data)
    return (
      <div>
        <PageHeader backTo="/books" backLabel="Zurück zu Bücher" title="Buch" />
        <ErrorState message={error} onRetry={reload} />
      </div>
    );
  if (!data) return <PageSkeleton />;

  const { book, titles, scenes } = data;

  return (
    <SaveBarProvider>
      <PageHeader
        backTo="/books"
        backLabel="Zurück zu Bücher"
        title={book.title}
        meta={<span className="font-mono">{book.slug}</span>}
        badges={
          book.shopifyProductHandles.length === 0 && (
            <StatusBadge tone="critical">Kein Shopify-Mapping</StatusBadge>
          )
        }
        actions={
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="secondary" disabled={deleting}>
                <MoreHorizontal />
                Weitere Aktionen
                <ChevronDown />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem variant="destructive" onSelect={handleDelete}>
                <Trash2 />
                Buch löschen
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        }
      />

      <Tabs value={tab} onValueChange={(v) => setTab(v)}>
        <TabsList className="mb-2">
          <TabsTrigger value="general">Allgemein</TabsTrigger>
          <TabsTrigger value="preview">Preview</TabsTrigger>
          <TabsTrigger value="scenes">Szenen ({scenes.length})</TabsTrigger>
        </TabsList>

        {/* forceMount: Formulare behalten ihre Entwürfe auch beim Tab-Wechsel. */}
        <TabsContent
          value="general"
          forceMount
          className="space-y-4 data-[state=inactive]:hidden"
        >
          <BookSettings book={book} onChange={reload} />
          <TitlesEditor bookId={book.id} titles={titles} onChange={reload} />
        </TabsContent>

        <TabsContent
          value="preview"
          forceMount
          className="space-y-4 data-[state=inactive]:hidden"
        >
          <PreviewCoverCard book={book} onChange={reload} />
          <PreviewTab book={book} onChange={reload} />
        </TabsContent>

        <TabsContent
          value="scenes"
          forceMount
          className="space-y-4 data-[state=inactive]:hidden"
        >
          <SceneSettingsCard book={book} onChange={reload} />
          <ScenesEditor book={book} scenes={scenes} onChange={reload} />
        </TabsContent>
      </Tabs>
    </SaveBarProvider>
  );
}

// ─── Gemeinsame Kleinteile ───────────────────────────────────────────────────

function DirtyBadge({ dirty }: { dirty: boolean }) {
  return dirty ? (
    <StatusBadge tone="warning">Nicht gespeichert</StatusBadge>
  ) : null;
}

function PathList({ paths }: { paths: (string | null | undefined)[] }) {
  const list = paths.filter(Boolean) as string[];
  if (list.length === 0) return null;
  return (
    <AdvancedSection label="Technische Details">
      <div className="space-y-1">
        {list.map((p) => (
          <code
            key={p}
            className="block font-mono text-xs break-all text-muted-foreground"
          >
            {p}
          </code>
        ))}
      </div>
    </AdvancedSection>
  );
}

function TimeoutField({
  id,
  label,
  value,
  placeholder,
  hint,
  onChange,
}: {
  id: string;
  label: string;
  value: number | null;
  placeholder: string;
  hint: string;
  onChange: (v: number | null) => void;
}) {
  return (
    <Field>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <Input
        id={id}
        type="number"
        min={1000}
        step={1000}
        className="bg-card"
        value={value ?? ""}
        placeholder={placeholder}
        onChange={(e) =>
          onChange(e.target.value === "" ? null : Number(e.target.value))
        }
      />
      <FieldDescription>{hint}</FieldDescription>
    </Field>
  );
}

function SimpleSelect<T extends string>({
  id,
  label,
  value,
  options,
  onChange,
  hint,
}: {
  id: string;
  label: string;
  value: T;
  options: readonly { value: T; label: string }[];
  onChange: (v: T) => void;
  hint?: string;
}) {
  return (
    <Field>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <Select value={value} onValueChange={(v) => onChange(v as T)}>
        <SelectTrigger id={id} className="w-full bg-card">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((o) => (
            <SelectItem key={o.value} value={o.value}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {hint && <FieldDescription>{hint}</FieldDescription>}
    </Field>
  );
}

const opts = <T extends string>(values: readonly T[]) =>
  values.map((v) => ({ value: v, label: v }));

// ─── Stammdaten ──────────────────────────────────────────────────────────────

function BookSettings({
  book,
  onChange,
}: {
  book: Book;
  onChange: () => void;
}) {
  const [draft, setDraft] = useState(book);
  const [handlesText, setHandlesText] = useState(
    (book.shopifyProductHandles ?? []).join("\n"),
  );
  const [err, setErr] = useState<string | null>(null);

  const parsedHandles = handlesText
    .split("\n")
    .map((l) => l.trim())
    .filter((l) => l.length > 0);
  const handlesDirty =
    parsedHandles.length !== (book.shopifyProductHandles ?? []).length ||
    parsedHandles.some((h, i) => h !== (book.shopifyProductHandles ?? [])[i]);

  const titleError = !draft.title.trim() ? "Bitte einen Titel angeben." : null;
  const dirty = draft.title !== book.title || handlesDirty;

  useEffect(() => {
    setDraft(book);
    setHandlesText((book.shopifyProductHandles ?? []).join("\n"));
  }, [book]);

  async function save(): Promise<boolean> {
    if (titleError) {
      toast.error(`Stammdaten: ${titleError}`);
      return false;
    }
    setErr(null);
    try {
      await booksApi.updateSettings(book.id, {
        title: draft.title,
        shopify_product_handles: parsedHandles,
      });
      toast.success("Stammdaten gespeichert");
      onChange();
      return true;
    } catch (e) {
      setErr((e as Error).message);
      toast.error(`Stammdaten: ${(e as Error).message}`);
      return false;
    }
  }

  useSaveBarForm({
    label: "Stammdaten",
    dirty,
    save,
    discard: () => {
      setDraft(book);
      setHandlesText((book.shopifyProductHandles ?? []).join("\n"));
      setErr(null);
    },
  });

  return (
    <FormSection title="Stammdaten" actions={<DirtyBadge dirty={dirty} />}>
      <FieldGroup>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field data-invalid={!!titleError}>
            <FieldLabel htmlFor="book-title">Titel</FieldLabel>
            <Input
              id="book-title"
              className="bg-card"
              value={draft.title}
              aria-invalid={!!titleError}
              onChange={(e) => setDraft({ ...draft, title: e.target.value })}
            />
            {titleError && <FieldError>{titleError}</FieldError>}
          </Field>
          <Field>
            <FieldLabel htmlFor="book-slug">Slug</FieldLabel>
            <Input
              id="book-slug"
              className="font-mono"
              value={draft.slug}
              disabled
              readOnly
            />
            <FieldDescription>Kann nicht geändert werden.</FieldDescription>
          </Field>
        </div>
        <Field>
          <FieldLabel htmlFor="book-handles">Shopify-Mapping</FieldLabel>
          <Textarea
            id="book-handles"
            rows={4}
            className="bg-card"
            value={handlesText}
            placeholder={"Kofi und das Brüllen der Savanne\nKOFI-HC\nKOFI-SC"}
            onChange={(e) => setHandlesText(e.target.value)}
          />
          <FieldDescription>
            Produkttitel, SKU oder Handle – einer pro Zeile,
            Groß-/Kleinschreibung egal.
            {parsedHandles.length === 0 &&
              " Ohne Mapping lassen sich Bestellungen nicht zuordnen."}
          </FieldDescription>
        </Field>
        {err && <p className="text-destructive">{err}</p>}
      </FieldGroup>
    </FormSection>
  );
}

// ─── Preview-Bilder und Schriften ────────────────────────────────────────────
// cover       = the finished illustration the preview edits: POST /preview keeps
//               it 1:1 and only swaps the character's head for the uploaded child.
// overlay     = transparent PNG laid on top of the finished render (title + logo).
// placeholder = what the customer sees while the render is still running.

const REMOVE_QUESTIONS: Record<PreviewAssetKind, string> = {
  cover:
    "Ohne Cover fällt die Preview-Generierung auf die alte Character-Generierung zurück.",
  placeholder:
    "Der Storefront bekommt während der Generierung dann kein Bild mehr (404).",
  overlay: "Previews kommen dann ohne Titel/Logo zurück.",
  "font-name": "Ohne Namens-Schrift wird kein Name ins Bild gerendert.",
  "font-prefix": "Das Präfix wird dann in der Namens-Schrift gesetzt.",
  "master-sheet":
    "Das Character-Sheet dieses Buchs wird dann wieder auf dem statischen Standard-Master gerendert – mit dem Outfit des Standard-Masters.",
};

const ASSET_LABELS: Record<PreviewAssetKind, string> = {
  cover: "Cover-Bild",
  overlay: "Overlay",
  placeholder: "Platzhalter",
  "font-name": "Schrift für den Namen",
  "font-prefix": "Schrift für das Präfix",
  "master-sheet": "Master-Sheet",
};

function PreviewCoverCard({
  book,
  onChange,
}: {
  book: Book;
  onChange: () => void;
}) {
  const confirm = useConfirm();
  const [coverUrl, setCoverUrl] = useState<string | null>(null);
  const [overlayUrl, setOverlayUrl] = useState<string | null>(null);
  const [placeholderUrl, setPlaceholderUrl] = useState<string | null>(null);
  const [masterUrl, setMasterUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const objectUrls: string[] = [];
    const load = (
      hasImage: boolean,
      kind: PreviewAssetKind,
      set: (u: string | null) => void,
    ) => {
      if (!hasImage) {
        set(null);
        return;
      }
      fetchAuthBlob(booksApi.previewAssetUrl(book.id, kind))
        .then((u) => {
          objectUrls.push(u);
          set(u);
        })
        .catch(() => set(null));
    };
    load(!!book.previewCoverPath, "cover", setCoverUrl);
    load(!!book.previewOverlayPath, "overlay", setOverlayUrl);
    load(!!book.previewPlaceholderPath, "placeholder", setPlaceholderUrl);
    load(!!book.characterMasterSheetPath, "master-sheet", setMasterUrl);
    return () => objectUrls.forEach((u) => URL.revokeObjectURL(u));
  }, [
    book.id,
    book.previewCoverPath,
    book.previewOverlayPath,
    book.previewPlaceholderPath,
    book.characterMasterSheetPath,
    book.updatedAt,
  ]);

  async function upload(kind: PreviewAssetKind, file: File) {
    setBusy(true);
    try {
      await booksApi.uploadPreviewAsset(book.id, kind, file);
      toast.success(`${ASSET_LABELS[kind]} hochgeladen`);
      onChange();
    } catch (e) {
      toast.error(`${ASSET_LABELS[kind]}: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }
  async function remove(kind: PreviewAssetKind) {
    const ok = await confirm({
      title: `${ASSET_LABELS[kind]} entfernen?`,
      description: REMOVE_QUESTIONS[kind],
      confirmLabel: "Entfernen",
      destructive: true,
    });
    if (!ok) return;
    setBusy(true);
    try {
      await booksApi.deletePreviewAsset(book.id, kind);
      toast.success(`${ASSET_LABELS[kind]} entfernt`);
      onChange();
    } catch (e) {
      toast.error(`${ASSET_LABELS[kind]}: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <FormSection
      title="Bilder und Schriften"
      description="Grundlage der Kunden-Preview. Overlay und Platzhalter im selben Seitenverhältnis wie das Cover anlegen."
    >
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <ImageSlot
          label="Cover-Bild (@img1)"
          hint="Fertige Illustration mit genau einem Character. Ersetzt werden nur Kopf, Gesicht, Haare und Augenfarbe."
          url={coverUrl}
          busy={busy}
          onUpload={(f) => upload("cover", f)}
          onDelete={() => remove("cover")}
          hasImage={!!book.previewCoverPath}
        />
        <ImageSlot
          label="Overlay"
          hint="Transparentes PNG mit Titel und Logo, wird nach der Generierung aufgelegt."
          url={overlayUrl}
          busy={busy}
          onUpload={(f) => upload("overlay", f)}
          onDelete={() => remove("overlay")}
          hasImage={!!book.previewOverlayPath}
        />
        <ImageSlot
          label="Platzhalter"
          hint="Sieht der Kunde, solange die Generierung läuft. Ohne Platzhalter antwortet der Endpoint mit 404."
          url={placeholderUrl}
          busy={busy}
          onUpload={(f) => upload("placeholder", f)}
          onDelete={() => remove("placeholder")}
          hasImage={!!book.previewPlaceholderPath}
        />
        <ImageSlot
          label="Master-Sheet (Character-Sheet)"
          hint="Basis des Character-Sheets: Layout, Posen und das Outfit dieses Buchs, Mannequin mit grauem Kopf. Ohne Upload gilt das statische Standard-Master."
          url={masterUrl}
          busy={busy}
          onUpload={(f) => upload("master-sheet", f)}
          onDelete={() => remove("master-sheet")}
          hasImage={!!book.characterMasterSheetPath}
        />
        <FontSlot
          label="Schrift für den Namen"
          hint=".ttf oder .otf – ohne Schrift wird kein Name gerendert."
          path={book.previewNameFontPath}
          busy={busy}
          onUpload={(f) => upload("font-name", f)}
          onDelete={() => remove("font-name")}
        />
        <FontSlot
          label={`Schrift für das Präfix („${book.previewSettings?.nameLayout?.prefixText ?? "mit"}“)`}
          hint="leer = gleiche Schrift wie der Name"
          path={book.previewPrefixFontPath}
          busy={busy}
          onUpload={(f) => upload("font-prefix", f)}
          onDelete={() => remove("font-prefix")}
        />
      </div>
      <PathList
        paths={[
          book.previewCoverPath,
          book.previewOverlayPath,
          book.previewPlaceholderPath,
          book.characterMasterSheetPath,
          book.previewNameFontPath,
          book.previewPrefixFontPath,
        ]}
      />
    </FormSection>
  );
}

function FilePickButton({
  label,
  accept,
  disabled,
  onPick,
}: {
  label: string;
  accept: string;
  disabled?: boolean;
  onPick: (file: File) => void;
}) {
  return (
    <Button size="sm" variant="secondary" asChild disabled={disabled}>
      <label
        className={
          disabled ? "pointer-events-none opacity-50" : "cursor-pointer"
        }
      >
        <Upload />
        {label}
        <input
          type="file"
          accept={accept}
          className="sr-only"
          disabled={disabled}
          onChange={(e) => {
            const f = e.target.files?.[0];
            // Zurücksetzen, damit dieselbe Datei erneut gewählt werden kann.
            e.target.value = "";
            if (f) onPick(f);
          }}
        />
      </label>
    </Button>
  );
}

function ImageSlot({
  label,
  hint,
  url,
  onUpload,
  onDelete,
  hasImage,
  busy,
}: {
  label: string;
  hint?: string;
  url: string | null;
  onUpload: (file: File) => void;
  onDelete: () => void;
  hasImage: boolean;
  busy?: boolean;
}) {
  return (
    <div className="flex flex-col overflow-hidden rounded-lg border bg-card">
      {url ? (
        <img
          className="aspect-[4/3] w-full bg-muted object-contain"
          src={url}
          alt={label}
        />
      ) : (
        <div className="flex aspect-[4/3] w-full flex-col items-center justify-center gap-1 bg-muted text-xs text-muted-foreground">
          <ImageIcon className="size-5" aria-hidden />
          kein Bild
        </div>
      )}
      <div className="flex flex-1 flex-col gap-2 p-3">
        <div>
          <div className="font-medium">{label}</div>
          {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
        </div>
        <div className="mt-auto flex flex-wrap gap-1.5">
          <FilePickButton
            label={hasImage ? "Ersetzen" : "Hochladen"}
            accept="image/png,image/jpeg,image/webp"
            disabled={busy}
            onPick={onUpload}
          />
          {hasImage && (
            <Button
              size="sm"
              variant="ghost"
              onClick={onDelete}
              disabled={busy}
            >
              Entfernen
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function FontSlot({
  label,
  hint,
  path,
  onUpload,
  onDelete,
  busy,
}: {
  label: string;
  hint: string;
  path: string | null;
  onUpload: (file: File) => void;
  onDelete: () => void;
  busy?: boolean;
}) {
  const filename = path ? path.split("/").pop() : null;
  return (
    <div className="flex flex-col overflow-hidden rounded-lg border bg-card">
      <div className="flex aspect-[4/3] w-full flex-col items-center justify-center gap-1 bg-muted p-3 text-center text-xs text-muted-foreground">
        <FileType className="size-5" aria-hidden />
        {filename ? (
          <strong className="text-foreground">{filename}</strong>
        ) : (
          "keine Schrift"
        )}
      </div>
      <div className="flex flex-1 flex-col gap-2 p-3">
        <div>
          <div className="font-medium">{label}</div>
          <p className="text-xs text-muted-foreground">{hint}</p>
        </div>
        <div className="mt-auto flex flex-wrap gap-1.5">
          <FilePickButton
            label={path ? "Ersetzen" : "Hochladen"}
            accept=".ttf,.otf,font/ttf,font/otf"
            disabled={busy}
            onPick={onUpload}
          />
          {path && (
            <Button
              size="sm"
              variant="ghost"
              onClick={onDelete}
              disabled={busy}
            >
              Entfernen
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Preview-Generierung + Name im Bild ──────────────────────────────────────
// Beide Karten schreiben dasselbe Objekt (preview_settings wird serverseitig
// komplett ersetzt). Deshalb ein gemeinsamer Entwurf und ein einziger
// Speichervorgang – sonst überschriebe der zweite Save den ersten.

function PreviewTab({ book, onChange }: { book: Book; onChange: () => void }) {
  const confirm = useConfirm();
  const stored = book.previewSettings ?? DEFAULT_PREVIEW_SETTINGS;
  const [draft, setDraft] = useState<PreviewSettings>(stored);
  const [extraText, setExtraText] = useState(extraToText(stored.openai.extra));
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    const s = book.previewSettings ?? DEFAULT_PREVIEW_SETTINGS;
    setDraft(s);
    setExtraText(extraToText(s.openai.extra));
  }, [book]);

  const next: PreviewSettings = {
    ...draft,
    openai: { ...draft.openai, extra: textToExtra(extraText) },
  };
  const storedName = stored.nameLayout ?? DEFAULT_NAME_LAYOUT;
  const nameDirty =
    JSON.stringify(draft.nameLayout ?? DEFAULT_NAME_LAYOUT) !==
    JSON.stringify(storedName);
  const settingsDirty =
    JSON.stringify({ ...next, nameLayout: null }) !==
    JSON.stringify({ ...stored, nameLayout: null });
  const dirty = nameDirty || settingsDirty;

  async function save(): Promise<boolean> {
    setErr(null);
    try {
      await booksApi.updateSettings(book.id, { preview_settings: next });
      toast.success("Preview-Einstellungen gespeichert");
      onChange();
      return true;
    } catch (e) {
      setErr((e as Error).message);
      toast.error(`Preview: ${(e as Error).message}`);
      return false;
    }
  }

  useSaveBarForm({
    label: [settingsDirty && "Preview-Generierung", nameDirty && "Name im Bild"]
      .filter(Boolean)
      .join(", "),
    dirty,
    save,
    discard: () => {
      setDraft(stored);
      setExtraText(extraToText(stored.openai.extra));
      setErr(null);
    },
  });

  function setGemini(patch: Partial<PreviewSettings["gemini"]>) {
    setDraft((d) => ({ ...d, gemini: { ...d.gemini, ...patch } }));
  }
  function setOpenai(patch: Partial<PreviewSettings["openai"]>) {
    setDraft((d) => ({ ...d, openai: { ...d.openai, ...patch } }));
  }

  async function resetGeneration() {
    const ok = await confirm({
      title: "Preview-Generierung auf Standard zurücksetzen?",
      description:
        "Provider, Timeout und Modellparameter werden auf die Standardwerte gesetzt. Gespeichert wird erst mit „Speichern“.",
      confirmLabel: "Zurücksetzen",
    });
    if (!ok) return;
    // „Name im Bild“ bleibt, wie er ist.
    setDraft((d) => ({
      ...DEFAULT_PREVIEW_SETTINGS,
      nameLayout: d.nameLayout,
    }));
    setExtraText("");
  }

  return (
    <>
      <FormSection
        title="Preview-Generierung"
        description="Gilt nur für die Kunden-Preview dieses Buchs."
        actions={
          <>
            <DirtyBadge dirty={settingsDirty} />
            <Button size="sm" variant="ghost" onClick={resetGeneration}>
              Auf Standard zurücksetzen
            </Button>
          </>
        }
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <SimpleSelect
            id="preview-provider"
            label="Provider"
            value={draft.provider}
            options={[
              { value: "gemini", label: "Gemini (Bild-Edit)" },
              {
                value: "openai",
                label: "OpenAI /v1/images/edits (gpt-image-2.5-flare)",
              },
            ]}
            onChange={(v) => setDraft({ ...draft, provider: v })}
          />
          <TimeoutField
            id="preview-timeout"
            label="Timeout (ms)"
            value={draft.timeoutMs}
            placeholder="z. B. 180000"
            hint="leer = globaler Wert. quality=high braucht deutlich länger als 30 s."
            onChange={(v) => setDraft({ ...draft, timeoutMs: v })}
          />
        </div>

        {draft.provider === "gemini" ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Field>
              <FieldLabel htmlFor="preview-gemini-model">Modell</FieldLabel>
              <Input
                id="preview-gemini-model"
                className="bg-card"
                value={draft.gemini.model}
                onChange={(e) => setGemini({ model: e.target.value })}
              />
            </Field>
            <SimpleSelect
              id="preview-gemini-aspect"
              label="Seitenverhältnis"
              hint="auto = aus dem Cover"
              value={draft.gemini.aspectRatio}
              options={opts(GEMINI_VALUES.aspectRatio)}
              onChange={(v) =>
                setGemini({
                  aspectRatio: v as PreviewSettings["gemini"]["aspectRatio"],
                })
              }
            />
            <SimpleSelect
              id="preview-gemini-size"
              label="Bildgröße"
              value={draft.gemini.imageSize}
              options={opts(GEMINI_VALUES.imageSize)}
              onChange={(v) =>
                setGemini({
                  imageSize: v as PreviewSettings["gemini"]["imageSize"],
                })
              }
            />
            <SimpleSelect
              id="preview-gemini-thinking"
              label="Thinking Level"
              value={draft.gemini.thinkingLevel}
              options={opts(GEMINI_VALUES.thinkingLevel)}
              onChange={(v) =>
                setGemini({ thinkingLevel: v as "LOW" | "MEDIUM" | "HIGH" })
              }
            />
          </div>
        ) : (
          <OpenAiParamFields
            value={draft.openai}
            onChange={setOpenai}
            idPrefix="preview-openai"
            extra={extraText}
            onExtraChange={setExtraText}
          />
        )}
        <p className="text-xs text-muted-foreground">
          „{EMPTY_LABEL}“ lässt den Parameter weg. Ausgegraute Felder
          unterstützt das gewählte Modell bzw. Format nicht; sie werden nicht
          mitgeschickt.
        </p>
        {err && <p className="text-destructive">{err}</p>}
      </FormSection>

      <PreviewNameCard
        book={book}
        layout={draft.nameLayout ?? DEFAULT_NAME_LAYOUT}
        dirty={nameDirty}
        onLayoutChange={(nameLayout) => setDraft((d) => ({ ...d, nameLayout }))}
      />
    </>
  );
}

// ─── Name im Bild ────────────────────────────────────────────────────────────
// Position/Größe/Farbe des Kindernamens auf dem fertigen Preview. Die Vorschau
// rendert serverseitig durch denselben Code wie der Kunden-Preview — kein
// Nachbau in CSS, der dann doch anders aussieht.

function PreviewNameCard({
  book,
  layout: draft,
  dirty,
  onLayoutChange,
}: {
  book: Book;
  layout: PreviewNameLayout;
  dirty: boolean;
  onLayoutChange: (layout: PreviewNameLayout) => void;
}) {
  const confirm = useConfirm();
  const [sampleName, setSampleName] = useState("Leon");
  const [sampleUrl, setSampleUrl] = useState<string | null>(null);
  const [rendering, setRendering] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const setDraft = onLayoutChange;

  // Debounced so dragging a slider doesn't fire a render per pixel.
  useEffect(() => {
    if (!book.previewCoverPath) return;
    let cancelled = false;
    let objectUrl: string | null = null;
    const timer = setTimeout(() => {
      setRendering(true);
      booksApi
        .nameSample(book.id, { name: sampleName, layout: draft })
        .then((url) => {
          if (cancelled) {
            URL.revokeObjectURL(url);
            return;
          }
          objectUrl = url;
          setSampleUrl(url);
          setErr(null);
        })
        .catch((e) => {
          if (!cancelled) setErr((e as Error).message);
        })
        .finally(() => {
          if (!cancelled) setRendering(false);
        });
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [
    book.id,
    book.previewCoverPath,
    book.previewNameFontPath,
    book.previewPrefixFontPath,
    draft,
    sampleName,
  ]);

  const num = (
    label: string,
    key: keyof PreviewNameLayout,
    {
      min,
      max,
      step = 0.1,
      hint,
    }: { min: number; max: number; step?: number; hint?: string },
  ) => (
    <Field key={key}>
      <div className="flex items-center justify-between gap-2">
        <FieldLabel htmlFor={`name-${key}`}>{label}</FieldLabel>
        <Input
          id={`name-${key}`}
          type="number"
          min={min}
          max={max}
          step={step}
          className="h-7 w-20 bg-card text-right"
          value={draft[key] as number}
          onChange={(e) =>
            setDraft({ ...draft, [key]: Number(e.target.value) })
          }
        />
      </div>
      <Slider
        min={min}
        max={max}
        step={step}
        value={[draft[key] as number]}
        onValueChange={([v]) => setDraft({ ...draft, [key]: v })}
        aria-label={label}
      />
      {hint && <FieldDescription>{hint}</FieldDescription>}
    </Field>
  );

  async function resetLayout() {
    const ok = await confirm({
      title: "Name im Bild auf Standard zurücksetzen?",
      description:
        "Alle Werte für Position, Größe und Schatten werden auf den Standard gesetzt. Gespeichert wird erst mit „Speichern“.",
      confirmLabel: "Zurücksetzen",
    });
    if (ok) setDraft(DEFAULT_NAME_LAYOUT);
  }

  return (
    <FormSection
      title="Name im Bild"
      description="Wie der Kindername auf das fertige Preview gesetzt wird."
      actions={
        <>
          <DirtyBadge dirty={dirty} />
          <Button size="sm" variant="ghost" onClick={resetLayout}>
            Auf Standard zurücksetzen
          </Button>
        </>
      }
    >
      {!book.previewNameFontPath && (
        <p className="rounded-lg bg-warning px-3 py-2 text-warning-foreground">
          Keine Namens-Schrift hinterlegt – es wird kein Name ins Bild
          gerendert. Unter „Bilder und Schriften“ hochladen.
        </p>
      )}
      <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
        <div className="grid content-start gap-4 sm:grid-cols-2">
          <Field>
            <FieldLabel htmlFor="name-prefix">Präfix</FieldLabel>
            <Input
              id="name-prefix"
              className="bg-card"
              value={draft.prefixText}
              onChange={(e) =>
                setDraft({ ...draft, prefixText: e.target.value })
              }
            />
            <FieldDescription>leer = nur der Name</FieldDescription>
          </Field>
          <Field>
            <FieldLabel htmlFor="name-color">Farbe</FieldLabel>
            <div className="flex items-center gap-2">
              <input
                id="name-color"
                type="color"
                className="h-8 w-12 cursor-pointer rounded-md border bg-card p-0.5"
                value={draft.color}
                onChange={(e) => setDraft({ ...draft, color: e.target.value })}
              />
              <code className="font-mono text-xs text-muted-foreground">
                {draft.color}
              </code>
            </div>
          </Field>
          {num("Schriftgröße", "fontSizePercent", {
            min: 1,
            max: 30,
            hint: "% der Bildhöhe",
          })}
          {num("Abstand von unten", "bottomOffsetPercent", {
            min: 0,
            max: 50,
            hint: "% der Bildhöhe bis zur Grundlinie",
          })}
          {num("Position X", "centerXPercent", {
            min: 0,
            max: 100,
            hint: "% der Breite (Mitte)",
          })}
          {num("Max. Breite", "maxWidthPercent", {
            min: 20,
            max: 100,
            step: 1,
            hint: "längere Namen werden kleiner",
          })}
          {num("Präfix-Größe", "prefixSizeRatio", {
            min: 0.2,
            max: 1.5,
            step: 0.01,
            hint: "relativ zum Namen",
          })}
          {num("Abstand Präfix–Name", "gapPercent", {
            min: 0,
            max: 100,
            step: 1,
            hint: "% der Schriftgröße",
          })}
          <Field orientation="horizontal" className="sm:col-span-2">
            <Switch
              id="name-shadow"
              checked={draft.shadowEnabled}
              onCheckedChange={(v) => setDraft({ ...draft, shadowEnabled: v })}
            />
            <FieldLabel htmlFor="name-shadow">Schatten</FieldLabel>
          </Field>
          {draft.shadowEnabled &&
            num("Schatten-Weichheit", "shadowBlurPercent", {
              min: 0,
              max: 3,
              step: 0.05,
            })}
          {draft.shadowEnabled &&
            num("Schatten-Stärke", "shadowOpacity", {
              min: 0,
              max: 1,
              step: 0.05,
            })}
        </div>

        <div className="space-y-2">
          <div className="text-xs font-medium text-muted-foreground">
            Vorschau {rendering && "· rendert…"}
          </div>
          {sampleUrl ? (
            <img
              src={sampleUrl}
              alt="Vorschau des Namens im Bild"
              className="w-full rounded-lg border"
            />
          ) : (
            <div className="flex aspect-square w-full items-center justify-center rounded-lg border bg-muted text-xs text-muted-foreground">
              {book.previewCoverPath ? "…" : "kein Cover hinterlegt"}
            </div>
          )}
          <Field>
            <FieldLabel htmlFor="name-sample">Beispielname</FieldLabel>
            <Input
              id="name-sample"
              className="bg-card"
              value={sampleName}
              onChange={(e) => setSampleName(e.target.value)}
            />
          </Field>
          {err && <p className="text-xs text-destructive">{err}</p>}
        </div>
      </div>
    </FormSection>
  );
}

// ─── Szenen-Generierung ──────────────────────────────────────────────────────
/**
 * Szenen-Generierung ("Character Replacement"): aus Ursprungszene (@img1) und
 * Charakter-Sheet (@img2) entsteht die fertige Buchseite. Läuft nach der
 * Bestellung automatisch für jede aktive Szene des Buchs.
 */
function SceneSettingsCard({
  book,
  onChange,
}: {
  book: Book;
  onChange: () => void;
}) {
  const confirm = useConfirm();
  const initial = book.sceneSettings ?? DEFAULT_SCENE_SETTINGS;
  const [draft, setDraft] = useState<SceneSettings>(initial);
  const [extraText, setExtraText] = useState(extraToText(initial.openai.extra));
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    const s = book.sceneSettings ?? DEFAULT_SCENE_SETTINGS;
    setDraft(s);
    setExtraText(extraToText(s.openai.extra));
  }, [book]);

  const next: SceneSettings = {
    ...draft,
    openai: { ...draft.openai, extra: textToExtra(extraText) },
  };
  const dirty =
    JSON.stringify(next) !==
    JSON.stringify(book.sceneSettings ?? DEFAULT_SCENE_SETTINGS);

  function setGemini(patch: Partial<SceneSettings["gemini"]>) {
    setDraft((d) => ({ ...d, gemini: { ...d.gemini, ...patch } }));
  }
  function setOpenai(patch: Partial<OpenAiImageParams>) {
    setDraft((d) => ({ ...d, openai: { ...d.openai, ...patch } }));
  }

  async function save(): Promise<boolean> {
    setErr(null);
    try {
      await booksApi.updateSettings(book.id, { scene_settings: next });
      toast.success("Szenen-Generierung gespeichert");
      onChange();
      return true;
    } catch (e) {
      setErr((e as Error).message);
      toast.error(`Szenen-Generierung: ${(e as Error).message}`);
      return false;
    }
  }

  useSaveBarForm({
    label: "Szenen-Generierung",
    dirty,
    save,
    discard: () => {
      const s = book.sceneSettings ?? DEFAULT_SCENE_SETTINGS;
      setDraft(s);
      setExtraText(extraToText(s.openai.extra));
      setErr(null);
    },
  });

  async function reset() {
    const ok = await confirm({
      title: "Szenen-Generierung auf Standard zurücksetzen?",
      description:
        "Provider, Timeout und Modellparameter werden auf die Standardwerte gesetzt. Gespeichert wird erst mit „Speichern“.",
      confirmLabel: "Zurücksetzen",
    });
    if (!ok) return;
    setDraft(DEFAULT_SCENE_SETTINGS);
    setExtraText("");
  }

  return (
    <FormSection
      title="Szenen-Generierung"
      description="Gilt für alle generierten Szenen dieses Buchs; einzelne Szenen können abweichen. Der Prompt steht unter Prompts → scene_placement."
      actions={
        <>
          <DirtyBadge dirty={dirty} />
          <Button size="sm" variant="ghost" onClick={reset}>
            Auf Standard zurücksetzen
          </Button>
        </>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <SimpleSelect
          id="scene-provider"
          label="Provider"
          value={draft.provider}
          options={[
            {
              value: "openai",
              label: "OpenAI /v1/images/edits (gpt-image-2.5-sunburst)",
            },
            { value: "gemini", label: "Gemini (Bild-Edit)" },
          ]}
          onChange={(v) => setDraft({ ...draft, provider: v })}
        />
        <TimeoutField
          id="scene-timeout"
          label="Timeout pro Szene (ms)"
          value={draft.timeoutMs}
          placeholder="z. B. 300000"
          hint="leer = globaler Wert"
          onChange={(v) => setDraft({ ...draft, timeoutMs: v })}
        />
      </div>

      {draft.provider === "gemini" ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Field>
            <FieldLabel htmlFor="scene-gemini-model">Modell</FieldLabel>
            <Input
              id="scene-gemini-model"
              className="bg-card"
              value={draft.gemini.model}
              onChange={(e) => setGemini({ model: e.target.value })}
            />
          </Field>
          <SimpleSelect
            id="scene-gemini-aspect"
            label="Seitenverhältnis"
            value={draft.gemini.aspectRatio}
            options={opts(SCENE_GEMINI_VALUES.aspectRatio)}
            onChange={(v) => setGemini({ aspectRatio: v })}
          />
          <SimpleSelect
            id="scene-gemini-size"
            label="Bildgröße"
            value={draft.gemini.imageSize}
            options={opts(SCENE_GEMINI_VALUES.imageSize)}
            onChange={(v) => setGemini({ imageSize: v })}
          />
          <SimpleSelect
            id="scene-gemini-thinking"
            label="Thinking Level"
            value={draft.gemini.thinkingLevel}
            options={opts(SCENE_GEMINI_VALUES.thinkingLevel)}
            onChange={(v) =>
              setGemini({ thinkingLevel: v as "LOW" | "MEDIUM" | "HIGH" })
            }
          />
        </div>
      ) : (
        <OpenAiParamFields
          value={draft.openai}
          onChange={setOpenai}
          idPrefix="scene-openai"
          extra={extraText}
          onExtraChange={setExtraText}
        />
      )}
      <p className="text-xs text-muted-foreground">
        Ursprungszene = @img1, Charakter-Sheet = @img2. Änderungen greifen ab
        dem nächsten Szenen-Job – auch für bereits eingereihte Bestellungen.
      </p>
      {err && <p className="text-destructive">{err}</p>}
    </FormSection>
  );
}

// ─── Druck-Titel ─────────────────────────────────────────────────────────────

const STATUS_LABELS: Record<BookTitle["status"], string> = {
  draft: "Entwurf",
  mastering_uploaded: "Mastering hochgeladen",
  tsa_pending: "TSA ausstehend",
  live: "Live",
  rejected: "Abgelehnt",
};

const STATUS_TONE: Record<BookTitle["status"], Tone> = {
  draft: "neutral",
  mastering_uploaded: "info",
  tsa_pending: "warning",
  live: "success",
  rejected: "critical",
};

function TitlesEditor({
  bookId,
  titles,
  onChange,
}: {
  bookId: string;
  titles: BookTitle[];
  onChange: () => void;
}) {
  const [adding, setAdding] = useState(false);
  const existing = titles.map((t) => t.binding);
  const canAdd = !(existing.includes("SC") && existing.includes("HC"));

  return (
    <FormSection
      title={`Druck-Titel (${titles.length})`}
      description="Je Bindung ein Titel mit eigener ISBN. Ohne Titel lassen sich Bestellungen nicht zuordnen."
      actions={
        canAdd && (
          <Button size="sm" variant="secondary" onClick={() => setAdding(true)}>
            <Plus />
            Titel hinzufügen
          </Button>
        )
      }
    >
      {titles.length === 0 ? (
        <EmptyState
          title="Noch keine Titel"
          description="Mindestens ein Titel (Softcover oder Hardcover) wird benötigt."
          action={
            <Button size="sm" onClick={() => setAdding(true)}>
              <Plus />
              Titel hinzufügen
            </Button>
          }
        />
      ) : (
        <div className="space-y-3">
          {titles.map((t) => (
            <TitleRow key={t.titleNumber} title={t} onChange={onChange} />
          ))}
        </div>
      )}
      <AddTitleDialog
        open={adding}
        onOpenChange={setAdding}
        bookId={bookId}
        existingBindings={existing}
        onCreated={() => {
          setAdding(false);
          toast.success("Titel angelegt");
          onChange();
        }}
      />
    </FormSection>
  );
}

function TitleRow({
  title,
  onChange,
}: {
  title: BookTitle;
  onChange: () => void;
}) {
  const confirm = useConfirm();
  const [draft, setDraft] = useState(title);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const dirty =
    draft.status !== title.status ||
    draft.heightMm !== title.heightMm ||
    draft.widthMm !== title.widthMm ||
    draft.totalExtent !== title.totalExtent ||
    draft.colourPages !== title.colourPages ||
    (draft.colourPagesPosition ?? "") !== (title.colourPagesPosition ?? "");

  useEffect(() => setDraft(title), [title]);

  async function save(): Promise<boolean> {
    setErr(null);
    try {
      await booksApi.updateTitle(title.titleNumber, {
        status: draft.status,
        height_mm: draft.heightMm,
        width_mm: draft.widthMm,
        total_extent: draft.totalExtent,
        colour_pages: draft.colourPages,
        colour_pages_position: draft.colourPagesPosition,
      });
      toast.success(`Titel ${title.titleNumber} gespeichert`);
      onChange();
      return true;
    } catch (e) {
      setErr((e as Error).message);
      toast.error(`Titel ${title.titleNumber}: ${(e as Error).message}`);
      return false;
    }
  }

  useSaveBarForm({
    label: `Titel ${title.binding}`,
    dirty,
    save,
    discard: () => {
      setDraft(title);
      setErr(null);
    },
  });

  async function remove() {
    const ok = await confirm({
      title: `Titel ${title.titleNumber} löschen?`,
      description: "Der Druck-Titel wird endgültig gelöscht.",
      confirmLabel: "Titel löschen",
      destructive: true,
    });
    if (!ok) return;
    setBusy(true);
    setErr(null);
    try {
      await booksApi.deleteTitle(title.titleNumber);
      toast.success(`Titel ${title.titleNumber} gelöscht`);
      onChange();
    } catch (e) {
      setErr((e as Error).message);
      toast.error((e as Error).message);
      setBusy(false);
    }
  }

  const prefix = `title-${title.titleNumber}`;
  const numField = (
    label: string,
    value: number | null,
    key: keyof BookTitle,
  ) => (
    <Field>
      <FieldLabel htmlFor={`${prefix}-${key}`}>{label}</FieldLabel>
      <Input
        id={`${prefix}-${key}`}
        type="number"
        className="bg-card"
        value={value ?? ""}
        onChange={(e) =>
          setDraft({
            ...draft,
            [key]: e.target.value ? Number(e.target.value) : null,
          })
        }
      />
    </Field>
  );

  return (
    <div className="rounded-lg border p-3">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <span className="font-mono font-semibold">{title.titleNumber}</span>
        <StatusBadge tone={title.binding === "HC" ? "warning" : "info"}>
          {title.binding === "HC" ? "Hardcover" : "Softcover"}
        </StatusBadge>
        <StatusBadge tone={STATUS_TONE[title.status]}>
          {STATUS_LABELS[title.status]}
        </StatusBadge>
        <DirtyBadge dirty={dirty} />
        <Button
          size="icon-sm"
          variant="ghost"
          className="ml-auto"
          onClick={remove}
          disabled={busy}
          aria-label={`Titel ${title.titleNumber} löschen`}
          title="Titel löschen"
        >
          <Trash2 />
        </Button>
      </div>
      <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <SimpleSelect
          id={`${prefix}-status`}
          label="Status"
          value={draft.status}
          options={(Object.keys(STATUS_LABELS) as BookTitle["status"][]).map(
            (s) => ({
              value: s,
              label: STATUS_LABELS[s],
            }),
          )}
          onChange={(v) => setDraft({ ...draft, status: v })}
        />
        {numField("Höhe (mm)", draft.heightMm, "heightMm")}
        {numField("Breite (mm)", draft.widthMm, "widthMm")}
        {numField("Seitenanzahl", draft.totalExtent, "totalExtent")}
        {numField("Farbseiten", draft.colourPages, "colourPages")}
        <Field>
          <FieldLabel htmlFor={`${prefix}-pos`}>Farbseiten-Position</FieldLabel>
          <Input
            id={`${prefix}-pos`}
            className="bg-card"
            value={draft.colourPagesPosition ?? ""}
            onChange={(e) =>
              setDraft({
                ...draft,
                colourPagesPosition: e.target.value || null,
              })
            }
          />
        </Field>
      </div>
      {err && <p className="mt-2 text-destructive">{err}</p>}
      <div className="mt-2">
        <PathList
          paths={[
            title.coverPdfPath,
            title.bookblockPdfPath,
            title.masteringXmlPath,
          ]}
        />
      </div>
    </div>
  );
}

function AddTitleDialog({
  open,
  onOpenChange,
  bookId,
  existingBindings,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  bookId: string;
  existingBindings: string[];
  onCreated: () => void;
}) {
  const firstFree: "SC" | "HC" = existingBindings.includes("SC") ? "HC" : "SC";
  const [binding, setBinding] = useState<"SC" | "HC">(firstFree);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setBinding(firstFree);
      setErr(null);
    }
  }, [open, firstFree]);

  async function save() {
    setBusy(true);
    setErr(null);
    try {
      await booksApi.createTitle(bookId, { binding });
      onCreated();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Druck-Titel hinzufügen</DialogTitle>
          <DialogDescription>
            Die ISBN wird automatisch vergeben.
          </DialogDescription>
        </DialogHeader>
        <SimpleSelect
          id="new-title-binding"
          label="Bindung"
          value={binding}
          options={[
            ...(!existingBindings.includes("SC")
              ? [{ value: "SC" as const, label: "Softcover (SC)" }]
              : []),
            ...(!existingBindings.includes("HC")
              ? [{ value: "HC" as const, label: "Hardcover (HC)" }]
              : []),
          ]}
          onChange={setBinding}
        />
        {err && <p className="text-destructive">{err}</p>}
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Abbrechen
          </Button>
          <Button onClick={save} disabled={busy}>
            {busy ? "Lege an…" : "Titel anlegen"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Szenen ──────────────────────────────────────────────────────────────────

function ScenesEditor({
  book,
  scenes,
  onChange,
}: {
  book: Book;
  scenes: BookScene[];
  onChange: () => void;
}) {
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const editing = scenes.find((s) => s.id === editingId) ?? null;
  const bookScene = book.sceneSettings ?? DEFAULT_SCENE_SETTINGS;

  const columns: ColumnDef<BookScene, unknown>[] = [
    {
      id: "index",
      header: "Nr.",
      meta: { className: "w-12" },
      cell: ({ row }) => row.original.index,
    },
    {
      id: "name",
      header: "Name",
      cell: ({ row }) => (
        <div className="leading-tight">
          <div className="flex items-center gap-1.5 font-medium">
            {row.original.name}
            {row.original.photoOutfitOnly && (
              <Badge variant="outline">nur Foto-Outfit</Badge>
            )}
          </div>
          {row.original.csvColumn !== row.original.name && (
            <div className="font-mono text-xs text-muted-foreground">
              {row.original.csvColumn}
            </div>
          )}
        </div>
      ),
    },
    {
      id: "kind",
      header: "Typ",
      cell: ({ row }) =>
        row.original.kind === "fixed" ? "Festes Bild" : "Generiert",
    },
    {
      id: "auto",
      header: "Automatisch",
      cell: ({ row }) =>
        row.original.kind === "fixed" ? (
          <span className="text-muted-foreground">—</span>
        ) : row.original.enabled ? (
          <StatusBadge tone="success">an</StatusBadge>
        ) : (
          <StatusBadge tone="warning">aus</StatusBadge>
        ),
    },
    {
      id: "image",
      header: "Bild",
      cell: ({ row }) => {
        const s = row.original;
        if (s.kind === "fixed")
          return s.fixedImagePath ? (
            <StatusBadge tone="success">vorhanden</StatusBadge>
          ) : (
            <StatusBadge tone="critical">fehlt</StatusBadge>
          );
        return s.baseImagePath ? (
          <StatusBadge tone="success">Ursprungszene</StatusBadge>
        ) : (
          <span className="text-muted-foreground">ohne</span>
        );
      },
    },
    {
      id: "overrides",
      header: "Abweichungen",
      cell: ({ row }) => {
        const o = migrateSceneOverrides(row.original).sceneOverrides;
        const parts = [
          o?.provider && `Provider ${o.provider}`,
          ...Object.entries(
            (o?.openai as Record<string, string> | undefined) ?? {},
          ).map(([k, v]) => `${k} ${v}`),
          ...Object.entries(
            (o?.gemini as Record<string, string> | undefined) ?? {},
          ).map(([k, v]) => `${k} ${v}`),
        ].filter(Boolean);
        return parts.length ? (
          <span className="text-xs">{parts.join(" · ")}</span>
        ) : (
          <span className="text-muted-foreground">
            Buch-Standard ({bookScene.provider})
          </span>
        );
      },
    },
  ];

  return (
    <FormSection
      title={`Szenen (${scenes.length})`}
      description="Klick auf eine Szene öffnet den Editor."
      actions={
        <Button size="sm" variant="secondary" onClick={() => setAdding(true)}>
          <Plus />
          Szene hinzufügen
        </Button>
      }
      contentClassName="px-0"
    >
      <DataTable
        columns={columns}
        data={scenes}
        getRowId={(s) => s.id}
        onRowClick={(s) => setEditingId(s.id)}
        empty={
          <EmptyState
            title="Noch keine Szenen"
            action={
              <Button size="sm" onClick={() => setAdding(true)}>
                <Plus />
                Szene hinzufügen
              </Button>
            }
          />
        }
      />
      {editing && (
        <SceneSheet
          key={editing.id}
          scene={editing}
          book={book}
          onChange={onChange}
          onClose={() => setEditingId(null)}
        />
      )}
      <AddSceneDialog
        open={adding}
        onOpenChange={setAdding}
        bookId={book.id}
        nextIndex={Math.max(0, ...scenes.map((s) => s.index)) + 1 || 1}
        onCreated={() => {
          setAdding(false);
          toast.success("Szene angelegt");
          onChange();
        }}
      />
    </FormSection>
  );
}

/**
 * Legacy `gemini_overrides` ({ aspectRatio, … }) → `scene_overrides.gemini`.
 * Nur die Werte, die das Szenen-Schema noch kennt; "auto" gibt es dort nicht.
 */
function migrateSceneOverrides(scene: BookScene): BookScene {
  if (scene.sceneOverrides || !scene.geminiOverrides) return scene;
  const legacy = scene.geminiOverrides as Record<string, unknown>;
  const gemini: Record<string, string> = {};
  for (const key of [
    "model",
    "aspectRatio",
    "imageSize",
    "thinkingLevel",
  ] as const) {
    const value = legacy[key];
    if (typeof value === "string" && value !== "auto") gemini[key] = value;
  }
  if (Object.keys(gemini).length === 0) return scene;
  return { ...scene, sceneOverrides: { gemini } as SceneOverrides };
}

// Radix-Select erlaubt keinen leeren Wert; "" = „Buch-Standard“.
const DEFAULT_OPTION = "__book__";

function SceneSheet({
  scene,
  book,
  onChange,
  onClose,
}: {
  scene: BookScene;
  book: Book;
  onChange: () => void;
  onClose: () => void;
}) {
  const confirm = useConfirm();
  // Alte gemini_overrides werden beim Anzeigen nach scene_overrides.gemini
  // gehoben; das nächste Speichern schreibt sie dorthin und leert die
  // Legacy-Spalte. Bis dahin liest der Server beide (scene_overrides gewinnt).
  const baseline = migrateSceneOverrides(scene);
  const [draft, setDraft] = useState(baseline);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [baseUrl, setBaseUrl] = useState<string | null>(null);
  const [fixedUrl, setFixedUrl] = useState<string | null>(null);

  const dirty =
    draft.index !== baseline.index ||
    draft.name !== baseline.name ||
    draft.csvColumn !== baseline.csvColumn ||
    draft.kind !== baseline.kind ||
    (draft.prompt ?? "") !== (baseline.prompt ?? "") ||
    (draft.sceneEmotion ?? "") !== (baseline.sceneEmotion ?? "") ||
    draft.orderIndex !== baseline.orderIndex ||
    draft.enabled !== baseline.enabled ||
    draft.photoOutfitOnly !== baseline.photoOutfitOnly ||
    JSON.stringify(draft.sceneOverrides ?? null) !==
      JSON.stringify(baseline.sceneOverrides ?? null);

  useEffect(() => setDraft(migrateSceneOverrides(scene)), [scene]);

  // Effektiver Provider dieser Szene: Szenen-Override, sonst Buch-Default.
  const bookScene = book.sceneSettings ?? DEFAULT_SCENE_SETTINGS;
  const provider = draft.sceneOverrides?.provider ?? bookScene.provider;

  function setOverrides(next: SceneOverrides) {
    setDraft({
      ...draft,
      sceneOverrides: Object.keys(next).length > 0 ? next : null,
    });
  }
  function setProviderOverride(value: string) {
    const next: SceneOverrides = { ...(draft.sceneOverrides ?? {}) };
    if (value === "gemini" || value === "openai") next.provider = value;
    else delete next.provider;
    setOverrides(next);
  }
  /** "" = kein Override, es gilt der Buch-Wert. */
  function setParamOverride(
    section: "gemini" | "openai",
    key: string,
    value: string,
  ) {
    const next: SceneOverrides = { ...(draft.sceneOverrides ?? {}) };
    const sub: Record<string, string> = {
      ...(next[section] as Record<string, string> | undefined),
    };
    if (value) sub[key] = value;
    else delete sub[key];
    if (Object.keys(sub).length > 0) next[section] = sub as never;
    else delete next[section];
    setOverrides(next);
  }
  const paramOverride = (section: "gemini" | "openai", key: string): string =>
    (draft.sceneOverrides?.[section] as Record<string, string> | undefined)?.[
      key
    ] ?? "";

  // Load thumbnails for images that exist.
  useEffect(() => {
    const revokes: string[] = [];
    if (scene.baseImagePath) {
      fetchAuthBlob(booksApi.sceneImageUrl(scene.id, "base"))
        .then((u) => {
          revokes.push(u);
          setBaseUrl(u);
        })
        .catch(() => setBaseUrl(null));
    } else setBaseUrl(null);
    if (scene.fixedImagePath) {
      fetchAuthBlob(booksApi.sceneImageUrl(scene.id, "fixed"))
        .then((u) => {
          revokes.push(u);
          setFixedUrl(u);
        })
        .catch(() => setFixedUrl(null));
    } else setFixedUrl(null);
    return () => revokes.forEach((u) => URL.revokeObjectURL(u));
  }, [scene.id, scene.baseImagePath, scene.fixedImagePath]);

  /** Fehler wurden vorher still verschluckt – jetzt immer sichtbar. */
  async function run(
    fn: () => Promise<unknown>,
    success: string,
  ): Promise<boolean> {
    setBusy(true);
    setErr(null);
    try {
      await fn();
      toast.success(success);
      onChange();
      return true;
    } catch (e) {
      setErr((e as Error).message);
      toast.error((e as Error).message);
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function save() {
    const ok = await run(
      () =>
        booksApi.updateScene(scene.id, {
          index: draft.index,
          name: draft.name,
          csv_column: draft.csvColumn,
          kind: draft.kind,
          prompt: draft.prompt ?? undefined,
          scene_emotion: draft.sceneEmotion ?? null,
          order_index: draft.orderIndex,
          enabled: draft.enabled,
          photo_outfit_only: draft.photoOutfitOnly,
          scene_overrides: draft.sceneOverrides,
          // Legacy-Spalte leeren — ihr Inhalt steckt jetzt in scene_overrides.
          gemini_overrides: null,
        }),
      `Szene ${draft.name} gespeichert`,
    );
    if (ok) onClose();
  }
  async function remove() {
    const ok = await confirm({
      title: `Szene „${scene.name}“ löschen?`,
      description: "Die Szene wird endgültig aus dem Buch entfernt.",
      confirmLabel: "Szene löschen",
      destructive: true,
    });
    if (!ok) return;
    if (await run(() => booksApi.deleteScene(scene.id), "Szene gelöscht"))
      onClose();
  }
  async function uploadImage(kind: "base" | "fixed", file: File) {
    await run(
      () => booksApi.uploadSceneImage(scene.id, file, kind),
      "Bild hochgeladen",
    );
  }
  async function deleteImage(kind: "base" | "fixed") {
    const ok = await confirm({
      title: "Bild entfernen?",
      description: "Das Bild wird von der Szene entfernt.",
      confirmLabel: "Bild entfernen",
      destructive: true,
    });
    if (!ok) return;
    await run(() => booksApi.deleteSceneImage(scene.id, kind), "Bild entfernt");
  }

  async function requestClose() {
    if (dirty) {
      const ok = await confirm({
        title: "Änderungen an der Szene verwerfen?",
        description: "Die Änderungen sind noch nicht gespeichert.",
        confirmLabel: "Verwerfen",
        cancelLabel: "Weiter bearbeiten",
        destructive: true,
      });
      if (!ok) return;
    }
    onClose();
  }

  const id = `scene-${scene.id}`;

  return (
    <Sheet open onOpenChange={(open) => !open && requestClose()}>
      <SheetContent className="w-full gap-0 sm:max-w-xl">
        <SheetHeader className="border-b">
          <SheetTitle>
            Szene {scene.index} · {scene.name}
          </SheetTitle>
          <SheetDescription>
            {draft.kind === "fixed"
              ? "Festes Bild – wird unverändert ins Buch übernommen."
              : "Wird aus Ursprungszene und Charakter-Sheet generiert."}
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 space-y-5 overflow-y-auto p-4">
          <FieldGroup>
            <div className="grid gap-4 sm:grid-cols-3">
              <Field>
                <FieldLabel htmlFor={`${id}-index`}>Nummer</FieldLabel>
                <Input
                  id={`${id}-index`}
                  type="number"
                  className="bg-card"
                  value={draft.index}
                  onChange={(e) =>
                    setDraft({ ...draft, index: Number(e.target.value) })
                  }
                />
              </Field>
              <Field>
                <FieldLabel htmlFor={`${id}-name`}>Name</FieldLabel>
                <Input
                  id={`${id}-name`}
                  className="bg-card"
                  value={draft.name}
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                />
              </Field>
              <Field>
                <FieldLabel htmlFor={`${id}-csv`}>CSV-Spalte</FieldLabel>
                <Input
                  id={`${id}-csv`}
                  className="bg-card font-mono"
                  value={draft.csvColumn}
                  onChange={(e) =>
                    setDraft({ ...draft, csvColumn: e.target.value })
                  }
                />
              </Field>
            </div>
            <SimpleSelect
              id={`${id}-kind`}
              label="Typ"
              value={draft.kind}
              options={[
                { value: "generated", label: "Generiert" },
                { value: "fixed", label: "Festes Bild" },
              ]}
              onChange={(v) => setDraft({ ...draft, kind: v })}
            />
            {draft.kind === "generated" && (
              <>
                <Field orientation="horizontal">
                  <Switch
                    id={`${id}-enabled`}
                    checked={draft.enabled}
                    onCheckedChange={(v) => setDraft({ ...draft, enabled: v })}
                  />
                  <div>
                    <FieldLabel htmlFor={`${id}-enabled`}>
                      Automatisch generieren
                    </FieldLabel>
                    <FieldDescription>
                      Nur für den automatischen Lauf bei Bestelleingang.
                      Inaktive Szenen lassen sich in der Bestellung jederzeit
                      manuell erzeugen.
                    </FieldDescription>
                  </div>
                </Field>
                <Field orientation="horizontal">
                  <Switch
                    id={`${id}-photo-outfit`}
                    checked={draft.photoOutfitOnly}
                    onCheckedChange={(v) =>
                      setDraft({ ...draft, photoOutfitOnly: v })
                    }
                  />
                  <div>
                    <FieldLabel htmlFor={`${id}-photo-outfit`}>
                      Nur bei „Outfit vom Foto“
                    </FieldLabel>
                    <FieldDescription>
                      Exklusive Outfit-Szene (z. B. nur die Füße zu sehen):
                      wird nur für Bestellungen mit Foto-Outfit generiert und
                      läuft über den Prompt „Outfit-Szenen“ statt den
                      Placement-Prompt. Bestehende Bestellungen ziehst du in der
                      Bestellung per „Outfit-Szenen nachgenerieren“ nach.
                    </FieldDescription>
                  </div>
                </Field>
                <Field>
                  <FieldLabel htmlFor={`${id}-emotion`}>
                    {draft.photoOutfitOnly
                      ? "Was die Seite zeigt"
                      : "Emotion in der Szene"}
                  </FieldLabel>
                  <Input
                    id={`${id}-emotion`}
                    className="bg-card"
                    value={draft.sceneEmotion ?? ""}
                    placeholder={
                      draft.photoOutfitOnly
                        ? "z. B. nur die Füße mit den Schuhen, die über den Waldboden laufen"
                        : "z. B. neugierig und aufgeregt, ängstlich, stolz lächelnd …"
                    }
                    onChange={(e) =>
                      setDraft({ ...draft, sceneEmotion: e.target.value })
                    }
                  />
                  <FieldDescription>
                    Fließt als <code>{"{{scene_emotion}}"}</code> in den
                    {draft.photoOutfitOnly
                      ? " Outfit-Szenen-Prompt."
                      : " globalen Placement-Prompt."}
                  </FieldDescription>
                </Field>
              </>
            )}
          </FieldGroup>

          {draft.kind === "generated" ? (
            <ImageSlot
              label="Ursprungszene (optional)"
              url={baseUrl}
              busy={busy}
              onUpload={(f) => uploadImage("base", f)}
              onDelete={() => deleteImage("base")}
              hasImage={!!scene.baseImagePath}
            />
          ) : (
            <ImageSlot
              label="Festes Bild (Pflicht)"
              url={fixedUrl}
              busy={busy}
              onUpload={(f) => uploadImage("fixed", f)}
              onDelete={() => deleteImage("fixed")}
              hasImage={!!scene.fixedImagePath}
            />
          )}

          {draft.kind === "generated" && (
            <AdvancedSection label="Abweichend vom Buch generieren">
              <div className="grid gap-4 sm:grid-cols-2">
                <SimpleSelect
                  id={`${id}-provider`}
                  label="Provider"
                  value={draft.sceneOverrides?.provider ?? DEFAULT_OPTION}
                  options={[
                    {
                      value: DEFAULT_OPTION,
                      label: `Buch-Standard (${bookScene.provider})`,
                    },
                    { value: "openai", label: "OpenAI" },
                    { value: "gemini", label: "Gemini" },
                  ]}
                  onChange={(v) =>
                    setProviderOverride(v === DEFAULT_OPTION ? "" : v)
                  }
                />
                {provider === "openai" ? (
                  <>
                    <SimpleSelect
                      id={`${id}-size`}
                      label="Größe"
                      value={paramOverride("openai", "size") || DEFAULT_OPTION}
                      options={[
                        {
                          value: DEFAULT_OPTION,
                          label: `Buch-Standard (${bookScene.openai.size})`,
                        },
                        ...OPENAI_VALUES.size.map((v) => ({
                          value: v,
                          label: v,
                        })),
                      ]}
                      onChange={(v) =>
                        setParamOverride(
                          "openai",
                          "size",
                          v === DEFAULT_OPTION ? "" : v,
                        )
                      }
                    />
                    <SimpleSelect
                      id={`${id}-quality`}
                      label="Qualität"
                      value={
                        paramOverride("openai", "quality") || DEFAULT_OPTION
                      }
                      options={[
                        {
                          value: DEFAULT_OPTION,
                          label: `Buch-Standard (${bookScene.openai.quality || "API-Standard"})`,
                        },
                        ...OPENAI_VALUES.quality
                          .filter((q) => q !== "")
                          .map((q) => ({ value: q, label: q })),
                      ]}
                      onChange={(v) =>
                        setParamOverride(
                          "openai",
                          "quality",
                          v === DEFAULT_OPTION ? "" : v,
                        )
                      }
                    />
                  </>
                ) : (
                  <SimpleSelect
                    id={`${id}-aspect`}
                    label="Seitenverhältnis"
                    value={
                      paramOverride("gemini", "aspectRatio") || DEFAULT_OPTION
                    }
                    options={[
                      {
                        value: DEFAULT_OPTION,
                        label: `Buch-Standard (${bookScene.gemini.aspectRatio})`,
                      },
                      ...SCENE_GEMINI_VALUES.aspectRatio.map((a) => ({
                        value: a,
                        label: a,
                      })),
                    ]}
                    onChange={(v) =>
                      setParamOverride(
                        "gemini",
                        "aspectRatio",
                        v === DEFAULT_OPTION ? "" : v,
                      )
                    }
                  />
                )}
              </div>
            </AdvancedSection>
          )}

          <PathList paths={[scene.baseImagePath, scene.fixedImagePath]} />
          {err && <p className="text-destructive">{err}</p>}
        </div>

        <SheetFooter className="flex-row border-t">
          <Button
            variant="ghost"
            className="mr-auto text-destructive"
            onClick={remove}
            disabled={busy}
          >
            <Trash2 />
            Löschen
          </Button>
          <Button variant="secondary" onClick={requestClose} disabled={busy}>
            Abbrechen
          </Button>
          <Button onClick={save} disabled={!dirty || busy}>
            {busy ? "Speichere…" : "Szene speichern"}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

function AddSceneDialog({
  open,
  onOpenChange,
  bookId,
  nextIndex,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  bookId: string;
  nextIndex: number;
  onCreated: () => void;
}) {
  const initial = () => ({
    index: nextIndex,
    name: `scene_${nextIndex}`,
    csv_column: `scene_${nextIndex}`,
    kind: "generated" as "generated" | "fixed",
    scene_emotion: "",
    order_index: nextIndex,
    enabled: true,
  });
  const [draft, setDraft] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setDraft(initial());
      setErr(null);
    }
    // initial hängt nur an nextIndex
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, nextIndex]);

  async function save() {
    setBusy(true);
    setErr(null);
    try {
      await booksApi.createScene(bookId, draft);
      onCreated();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Szene hinzufügen</DialogTitle>
          <DialogDescription>
            Bilder lädst du danach im Szenen-Editor hoch.
          </DialogDescription>
        </DialogHeader>
        <FieldGroup>
          <div className="grid gap-4 sm:grid-cols-3">
            <Field>
              <FieldLabel htmlFor="new-scene-index">Nummer</FieldLabel>
              <Input
                id="new-scene-index"
                type="number"
                className="bg-card"
                value={draft.index}
                onChange={(e) =>
                  setDraft({ ...draft, index: Number(e.target.value) })
                }
              />
            </Field>
            <Field>
              <FieldLabel htmlFor="new-scene-name">Name</FieldLabel>
              <Input
                id="new-scene-name"
                className="bg-card"
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              />
            </Field>
            <Field>
              <FieldLabel htmlFor="new-scene-csv">CSV-Spalte</FieldLabel>
              <Input
                id="new-scene-csv"
                className="bg-card font-mono"
                value={draft.csv_column}
                onChange={(e) =>
                  setDraft({ ...draft, csv_column: e.target.value })
                }
              />
            </Field>
          </div>
          <SimpleSelect
            id="new-scene-kind"
            label="Typ"
            value={draft.kind}
            options={[
              { value: "generated", label: "Generiert" },
              { value: "fixed", label: "Festes Bild" },
            ]}
            onChange={(v) => setDraft({ ...draft, kind: v })}
          />
          {draft.kind === "generated" && (
            <Field>
              <FieldLabel htmlFor="new-scene-emotion">
                Emotion in der Szene
              </FieldLabel>
              <Input
                id="new-scene-emotion"
                className="bg-card"
                value={draft.scene_emotion}
                placeholder="z. B. neugierig und aufgeregt, ängstlich, stolz lächelnd …"
                onChange={(e) =>
                  setDraft({ ...draft, scene_emotion: e.target.value })
                }
              />
              <FieldDescription>
                Der Placement-Prompt selbst wird global unter Prompts gepflegt.
              </FieldDescription>
            </Field>
          )}
        </FieldGroup>
        {err && <p className="text-destructive">{err}</p>}
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Abbrechen
          </Button>
          <Button onClick={save} disabled={busy}>
            {busy ? "Lege an…" : "Szene anlegen"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
